import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    VerifiedUser,
    TrendingDown,
    Refresh,
    LocationOn,
    Groups,
    CameraAlt,
    RocketLaunch,
    Visibility,
    Storefront,
    Percent,
    Language,
    ShoppingBag,
    Hub
} from '@mui/icons-material';

const FashionIndustryPage = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    return (
        <div className="font-sans text-gray-900 bg-white">
            {/* HERO SECTION */}
            <section className="bg-[hsl(264,50%,96%)] py-24 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/30 to-transparent pointer-events-none"></div>
                <div className="container mx-auto px-6 max-w-7xl text-center relative z-10">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6 bg-gradient-to-r from-gray-900 via-primary-700 to-[#F482C0] bg-clip-text text-transparent pb-2">
                            Influencer Marketing for <br /> <span className="text-[#F482C0]">Apparel & Fashion Brands</span>
                        </h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed font-light text-xl">
                            Build trust, drive local buzz, and convert attention into sales using <span className="font-bold text-gray-900">hyperlocal fashion creators</span> with Oye Creators. From streetwear labels to premium fashion brands, we help you win customers where they live, shop, and scroll.
                        </p>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Link to="/contact" className="inline-block px-10 py-4 bg-primary-500 text-white rounded-full font-bold hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/30 text-lg">
                                Start a Fashion Campaign
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* INDUSTRY CHALLENGES */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-7xl">
                    <motion.h2
                        className="text-3xl font-bold text-center mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        Challenges Fashion & Apparel Brands Face Today
                    </motion.h2>

                    <motion.div
                        className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div variants={fadeInUp} className="p-8 border border-gray-100 rounded-3xl bg-white shadow-sm hover:shadow-xl transition-all group">
                            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-red-500 mb-6 group-hover:scale-110 transition-transform">
                                <VerifiedUser fontSize="large" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Low Trust in Ads</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">Fashion ads look polished, but audiences trust real people wearing real outfits more.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="p-8 border border-gray-100 rounded-3xl bg-white shadow-sm hover:shadow-xl transition-all group">
                            <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center text-orange-500 mb-6 group-hover:scale-110 transition-transform">
                                <TrendingDown fontSize="large" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">High CAC</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">Paid ads are expensive, while conversion rates continue to decline across platforms.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="p-8 border border-gray-100 rounded-3xl bg-white shadow-sm hover:shadow-xl transition-all group">
                            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-500 mb-6 group-hover:scale-110 transition-transform">
                                <Refresh fontSize="large" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Short Trend Cycles</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">Styles change fast. Brands need rapid, scalable content creation to stay relevant.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="p-8 border border-gray-100 rounded-3xl bg-white shadow-sm hover:shadow-xl transition-all group">
                            <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center text-green-500 mb-6 group-hover:scale-110 transition-transform">
                                <LocationOn fontSize="large" />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Local Relevance</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">Fashion choices vary city by city. Generic national campaigns fail to connect locally.</p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* OYE CREATORS SOLUTION */}
            <section className="py-20 bg-[#F9B4DA1A]">
                <div className="container mx-auto px-6 max-w-7xl">
                    <motion.div
                        className="text-center max-w-3xl mx-auto mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-3xl font-bold mb-6">How Oye Creators Helps Fashion Brands Grow</h2>
                        <p className="text-lg text-gray-600">
                            Oye Creators connects apparel brands with <span className="font-bold text-gray-900">local fashion influencers, college creators, lifestyle vloggers, and style enthusiasts</span> who influence buying decisions daily.
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid md:grid-cols-2 gap-8"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div variants={fadeInUp} className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-md transition-shadow flex items-start gap-6 border border-white hover:border-pink-100">
                            <div className="p-4 bg-purple-100 text-purple-600 rounded-2xl shrink-0">
                                <Groups fontSize="large" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold mb-2">Hyperlocal Creator Network</h3>
                                <p className="text-gray-600 leading-relaxed">Reach creators from specific cities, colleges, malls, and neighborhoods.</p>
                            </div>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-md transition-shadow flex items-start gap-6 border border-white hover:border-pink-100">
                            <div className="p-4 bg-pink-100 text-pink-600 rounded-2xl shrink-0">
                                <CameraAlt fontSize="large" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold mb-2">Authentic Try-On Content</h3>
                                <p className="text-gray-600 leading-relaxed">Real people styling your outfits — reels, stories, photos & store visits.</p>
                            </div>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-md transition-shadow flex items-start gap-6 border border-white hover:border-pink-100">
                            <div className="p-4 bg-indigo-100 text-indigo-600 rounded-2xl shrink-0">
                                <RocketLaunch fontSize="large" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold mb-2">Scalable Campaigns</h3>
                                <p className="text-gray-600 leading-relaxed">Run 10, 50, or 500 creators simultaneously without operational complexity.</p>
                            </div>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="bg-white p-10 rounded-3xl shadow-sm hover:shadow-md transition-shadow flex items-start gap-6 border border-white hover:border-pink-100">
                            <div className="p-4 bg-teal-100 text-teal-600 rounded-2xl shrink-0">
                                <Visibility fontSize="large" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold mb-2">Performance Transparency</h3>
                                <p className="text-gray-600 leading-relaxed">Track reach, engagement, content delivery, and campaign impact clearly.</p>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* USE CASES */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-7xl">
                    <motion.h2
                        className="text-3xl font-bold text-center mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        Use Cases for Apparel & Fashion Brands
                    </motion.h2>
                    <motion.div
                        className="grid md:grid-cols-4 gap-6"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div variants={fadeInUp} className="border border-gray-200 p-8 rounded-3xl hover:border-primary-500 transition-colors group">
                            <div className="mb-6 text-gray-400 group-hover:text-primary-600 transition-colors">
                                <RocketLaunch fontSize="large" />
                            </div>
                            <h3 className="text-lg font-bold mb-3">New Collection Launch</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">Create city-wide buzz with creators posting outfits on launch week.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="border border-gray-200 p-8 rounded-3xl hover:border-primary-500 transition-colors group">
                            <div className="mb-6 text-gray-400 group-hover:text-primary-600 transition-colors">
                                <Storefront fontSize="large" />
                            </div>
                            <h3 className="text-lg font-bold mb-3">Store Opening Promotions</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">Drive footfall using nearby creators visiting and styling from your store.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="border border-gray-200 p-8 rounded-3xl hover:border-primary-500 transition-colors group">
                            <div className="mb-6 text-gray-400 group-hover:text-primary-600 transition-colors">
                                <Percent fontSize="large" />
                            </div>
                            <h3 className="text-lg font-bold mb-3">Festive & Seasonal Sales</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">Boost Diwali, Eid, wedding, and end-of-season sales with relatable creators.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="border border-gray-200 p-8 rounded-3xl hover:border-primary-500 transition-colors group">
                            <div className="mb-6 text-gray-400 group-hover:text-primary-600 transition-colors">
                                <Language fontSize="large" />
                            </div>
                            <h3 className="text-lg font-bold mb-3">D2C Brand Awareness</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">Generate UGC that fuels ads, website trust, and social proof.</p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* WHO IT IS FOR */}
            <section className="py-20 bg-gray-900 text-white">
                <div className="container mx-auto px-6 max-w-7xl">
                    <motion.h2
                        className="text-3xl font-bold text-center mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        Who Should Use Oye Creators
                    </motion.h2>
                    <motion.div
                        className="grid md:grid-cols-4 gap-6"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div variants={fadeInUp} className="bg-gray-800 p-8 rounded-3xl hover:bg-gray-700 transition-colors border border-gray-700 hover:border-primary-500/50">
                            <div className="text-primary-400 mb-4"><ShoppingBag fontSize="large" /></div>
                            <h3 className="text-lg font-bold mb-3">Local Fashion Stores</h3>
                            <p className="text-sm text-gray-400 leading-relaxed">Boutiques, ethnic wear stores, streetwear & designer studios.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="bg-gray-800 p-8 rounded-3xl hover:bg-gray-700 transition-colors border border-gray-700 hover:border-primary-500/50">
                            <div className="text-primary-400 mb-4"><Language fontSize="large" /></div>
                            <h3 className="text-lg font-bold mb-3">D2C Apparel Brands</h3>
                            <p className="text-sm text-gray-400 leading-relaxed">Online-first brands looking for trust-driven customer acquisition.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="bg-gray-800 p-8 rounded-3xl hover:bg-gray-700 transition-colors border border-gray-700 hover:border-primary-500/50">
                            <div className="text-primary-400 mb-4"><Storefront fontSize="large" /></div>
                            <h3 className="text-lg font-bold mb-3">Retail Chains</h3>
                            <p className="text-sm text-gray-400 leading-relaxed">City-wise influencer campaigns for consistent store-level growth.</p>
                        </motion.div>
                        <motion.div variants={fadeInUp} className="bg-gray-800 p-8 rounded-3xl hover:bg-gray-700 transition-colors border border-gray-700 hover:border-primary-500/50">
                            <div className="text-primary-400 mb-4"><Hub fontSize="large" /></div>
                            <h3 className="text-lg font-bold mb-3">Fashion Marketplaces</h3>
                            <p className="text-sm text-gray-400 leading-relaxed">Seller-focused influencer content for catalog visibility.</p>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* WHY OYE CREATORS */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-4xl text-center">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-3xl font-bold mb-10">Why Fashion Brands Choose Oye Creators</h2>
                        <div className="flex flex-wrap justify-center gap-4">
                            {[
                                "Access to 5K–50K follower creators",
                                "High local influence",
                                "Lower cost than celebrities",
                                "Authentic content quality",
                                "Fast multi-city execution",
                                "Reels-first approach"
                            ].map((item, i) => (
                                <motion.span
                                    key={i}
                                    className="px-6 py-3 bg-[hsl(264,50%,96%)] rounded-full text-gray-800 font-medium border border-transparent hover:border-primary-300 transition-colors"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    ✅ {item}
                                </motion.span>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-primary-500 text-white py-24 text-center">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                    >
                        <h2 className="text-3xl md:text-5xl font-extrabold mb-6 text-white">Ready to Grow Your Fashion Brand?</h2>
                        <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10">
                            Launch your first apparel influencer campaign with Oye Creators today.
                        </p>
                        <motion.div whileHover={{ scale: 1.05 }}>
                            <Link to="/contact" className="inline-block px-10 py-5 bg-white text-primary-600 rounded-full font-bold text-lg hover:bg-gray-100 transition-colors shadow-2xl">
                                Talk to Our Team
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default FashionIndustryPage;
